"use client"

import { useEffect, useState } from "react"
import { useAuction } from "@/hooks/use-auction"
import { serverNow } from "@/lib/clock-sync"
import {
  deriveViewerAuctionState,
  type ViewerAuctionBaseline,
  type ViewerAuctionState,
} from "@/lib/auction-viewer-state"

export interface UseViewerAuctionStateInputs {
  listingId: string
  currentUserId: string | null
  auction: ViewerAuctionBaseline
  sellerId: string
  /**
   * Optional: paid-but-unsettled order id discovered out of band (e.g. by the
   * checkout sheet via `current-bid` API). Surfaced on winner states so the
   * sheet can render its retry-settlement UI.
   */
  existingOrderId?: string | null
  /**
   * Server-truth overrides — see `DeriveViewerAuctionStateInputs.overrides`.
   * Used by the checkout sheet, which receives `reserveNotMet` /
   * `deadlineExpired` as direct booleans from `/current-bid`.
   */
  overrides?: { reserveNotMet?: boolean; deadlineExpired?: boolean }
}

/**
 * Single source of truth for "what should this viewer see for this auction?".
 * Subscribes to the realtime slot and projects it (plus SSR baseline + viewer
 * identity) into a discriminated union. All visible auction surfaces should
 * switch on `state.kind` instead of re-deriving booleans from raw fields.
 *
 * See `lib/auction-viewer-state.ts` for the FSM definition and tests.
 */
export function useViewerAuctionState({
  listingId,
  currentUserId,
  auction,
  sellerId,
  existingOrderId = null,
  overrides,
}: UseViewerAuctionStateInputs): ViewerAuctionState {
  const slot = useAuction(listingId)

  // Force re-derivation when the wall clock crosses an "interesting" boundary
  // (auction end or payment deadline). Without this, the FSM would stay "live"
  // for the few-hundred-ms-to-3s window between countdown=0 client-side and
  // the transition endpoint / cron flipping `auctionStatus` to "ended".
  const [, forceTick] = useState(0)
  const effectiveEndAt = slot?.endAt ?? auction.endAt
  const effectiveDeadline = slot?.paymentDeadline ?? auction.paymentDeadline
  useEffect(() => {
    const targets = [effectiveEndAt, effectiveDeadline]
      .map((s) => (s ? new Date(s).getTime() : null))
      .filter((t): t is number => t != null)
    const now = serverNow()
    const future = targets.filter((t) => t > now)
    if (future.length === 0) return
    const handle = setTimeout(
      () => forceTick((n) => n + 1),
      Math.min(...future) - now + 50,
    )
    return () => clearTimeout(handle)
  }, [effectiveEndAt, effectiveDeadline])

  return deriveViewerAuctionState({
    slot,
    auction,
    currentUserId,
    sellerId,
    existingOrderId,
    overrides,
    now: serverNow,
  })
}
